import { useEffect, useRef, useState } from 'react'

export interface FetchState<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Generic data-fetching hook.
 *
 * - Re-runs whenever `deps` change (same semantics as useEffect deps).
 * - Prevents stale responses: if the component unmounts or deps change before
 *   a response arrives, the result is silently discarded.
 * - `refetch()` triggers a fresh request without changing deps.
 * - `fetcher` is captured via ref so it can safely close over local state
 *   without needing to be listed in deps.
 */
export function useFetch<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): FetchState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  // Always keep the latest fetcher without adding it to effect deps.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    fetcherRef.current()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Something went wrong.')
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return {
    data,
    loading,
    error,
    refetch: () => setTick((t) => t + 1),
  }
}
